import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import { Accordion, Select } from '@loadsmart/loadsmart-ui'
import { type Selectable } from '@loadsmart/loadsmart-ui/dist/hooks/useSelectable'
import type EventLike from '@loadsmart/loadsmart-ui/dist/utils/types/EventLike'
import { Text, Layout, Button } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-react/dist/tokens'

import { RequestStatus } from 'core/domain/Request'
import SelectDriverImage from 'core/ui/assets/select-driver.svg'
import { AnimatedArrowIcon } from 'core/ui/components/animated-arrow-icon/AnimatedArrowIcon'
import { Icon } from 'core/ui/components/icon/Icon'

import { AccordionBodyWrapper } from 'loads/ui/load-setup/LoadSetup.styles'

import { type Employee } from '../domain/Employee'
import { mapEmployeesToSelect } from '../presenters/employeesPresenter'
import { useGetCarrierEmployees } from '../ui/hooks/useGetCarrierEmployees'
import DriverForm from './DriverForm'
import { useAssignDriver } from './hooks/useAssignDriver'

export type DriverSelectorProps = {
  carrierUUID: string
  shipmentUUID: string
  driver?: Employee
  onAssignDriver: () => void
}

type SelectedDriver = {
  label: string
  value: string
}

const ColoredIconCheck = styled(Icon)`
  color: rgb(${getToken('color-success-100')});
`

function CreatableOption() {
  return <Select.CreatableOption>Add new driver</Select.CreatableOption>
}

export default function DriverSelector({
  carrierUUID,
  shipmentUUID,
  driver,
  onAssignDriver,
}: DriverSelectorProps) {
  const { employees } = useGetCarrierEmployees(carrierUUID)
  const [selectedDriver, setSelectedDriver] = useState<SelectedDriver | null>()
  const [isOpened, setIsOpened] = useState<boolean>(false)
  const [isCreatingDriver, setIsCreatingDriver] = useState<boolean>(false)
  const { patchAssignDriver, requestStatus } = useAssignDriver()
  const employeesOptions = mapEmployeesToSelect(employees)
  const isPending = requestStatus === RequestStatus.Pending
  const disableSubmit = !selectedDriver || isPending

  const submitAssignedDriver = useCallback(async () => {
    if (!selectedDriver?.value) return

    await patchAssignDriver(selectedDriver.value, shipmentUUID)
  }, [patchAssignDriver, selectedDriver?.value, shipmentUUID])

  function handleExpandChange(opened: boolean) {
    setIsOpened(opened)
  }

  function handleCreateDriver() {
    setIsCreatingDriver(true)
  }

  function onDriverChanged(event: EventLike<Selectable | Selectable[] | null>) {
    setSelectedDriver(event.target.value as SelectedDriver)
  }

  const setDriver = useCallback((employee: Employee) => {
    setSelectedDriver({
      label: employee.name ?? employee.phoneNumber,
      value: employee.uuid,
    })
  }, [])

  const onDriverSaved = useCallback(
    (employee: Employee) => {
      setDriver(employee)
      handleExpandChange(false)
      onAssignDriver()
    },
    [onAssignDriver, setDriver]
  )

  useEffect(() => {
    if (driver && selectedDriver === undefined) {
      setDriver(driver)
      handleExpandChange(false)
    }
  }, [driver, selectedDriver, setDriver])

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      handleExpandChange(false)
      toast.success('Driver assigned with success')
      onAssignDriver()
    }
  }, [onAssignDriver, requestStatus])

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Failed to assign driver, try again later')
    }
  }, [requestStatus])

  return (
    <Accordion expanded={isOpened} onExpandedChange={handleExpandChange}>
      <Accordion.Toggle
        leading={driver ? <ColoredIconCheck name="check" width="16" height="16" /> : null}
        trailing={AnimatedArrowIcon}
      >
        <Text variant="heading-sm">Select driver</Text>
        {driver?.name && <Text variant="heading-sm">- {driver.name}</Text>}
      </Accordion.Toggle>
      <AccordionBodyWrapper $overflow={isOpened ? 'initial' : 'auto'}>
        <Accordion.Body>
          <Layout.Group gap="spacing-6">
            <img
              width={185}
              src={SelectDriverImage}
              alt="Driver Selection"
              style={{ alignSelf: 'center' }}
            />
            <Layout.Stack align="flex-start">
              <Layout.Stack gap="spacing-1">
                <Text variant="body-md" color="color-text-tertiary">
                  <label htmlFor="driver-select">Tell us who will haul this load.</label>
                </Text>
                <Select
                  id="driver-select"
                  name="driver-select"
                  placeholder="Select driver"
                  value={selectedDriver as Selectable}
                  onChange={onDriverChanged}
                  options={employeesOptions}
                  createOptionPosition="first"
                  onCreate={handleCreateDriver}
                  isValidNewOption={true}
                  components={{ CreatableOption }}
                />
                <Text variant="body-sm">
                  We use the driver's phone number to send our app link and track the load
                  automatically
                </Text>
              </Layout.Stack>
              {isCreatingDriver && (
                <DriverForm
                  carrierUUID={carrierUUID}
                  shipmentUUID={shipmentUUID}
                  onDriverSaved={onDriverSaved}
                />
              )}
              {!isCreatingDriver && (
                <Button
                  type="button"
                  variant="primary"
                  disabled={disableSubmit}
                  onClick={submitAssignedDriver}
                  loading={isPending}
                >
                  Assign driver and save
                </Button>
              )}
            </Layout.Stack>
          </Layout.Group>
        </Accordion.Body>
      </AccordionBodyWrapper>
    </Accordion>
  )
}
